import React, { useState } from 'react';
import { IpaType } from '../phonemes/ipa';
import { allFilterProps, allSortProps, FilterProp, SortProp, TrackLanguageFeatures } from '../world/trackLanguageFeatures';
import { IpaChart } from './chart/IpaChart';
import { ChoiceList } from './widgets/ChoiceList';
import { SummaryChart } from './chart/SummaryChart';
import { SegmentFeatures } from './chart/SegmentFeatures';
import { getPhoibleUrl } from '../world/languageToPhoibleId';

export function LanguageCharts(props: {
  readonly trackers: TrackLanguageFeatures,
  /** called when a symbol is clicked on */
  readonly clicked?: (ipa: IpaType) => void;
  /** true if a call to 'clicked' will do something useful */
  readonly canClick?: (ipa: IpaType) => boolean;
}) {
  const [language, setLanguage] = useState('American English');
  const [sortBy, setSortBy] = useState('name');
  const [languageList, setLanguageList] = useState(props.trackers.sortedByName());
  const [filterBy, setFilterBy] = useState('all');
  const [customFilter, setCustomFilter] = useState('');
  const [customError, setCustomError] = useState('');
  const [hoveredSegment, setHoveredSegment] = useState('');

  const stats = props.trackers.lookupStats(language);

  const phoibleId = getPhoibleUrl(language);
  const link = phoibleId === null ? "reference link" : <a href={phoibleId}>reference link</a>;

  function changeSortBy(prop: string) {
    setSortBy(prop);
    makeList(prop, filterBy, customFilter);
  }

  function changeFilterBy(prop: string) {
    setFilterBy(prop);
    makeList(sortBy, prop, customFilter);
  }

  function applyCustomFilter(value: string) {
    setFilterBy('custom');
    setCustomFilter(value);
    makeList(sortBy, 'custom', value);
  }

  /** sort & filter the list of languages */
  function makeList(sortProp: string, filterProp: string, customFilter: string) {
    const sorted = props.trackers.sortedByCount(sortProp as SortProp);
    const filtered = filterProp === 'custom'
      ? props.trackers.customFilter(customFilter, sorted)
      : props.trackers.filteredByProp(filterProp as FilterProp, sorted);
    if ('unknown' in filtered) {
      setCustomError(filtered.unknown.join(' '));
    } else {
      setCustomError('');
      setLanguageList(filtered);
      setLanguage(filtered[0]);
    }
  }

  function lookup(ipa: IpaType) { return stats.chartIpa.includes(ipa) ? ipa : '-'; }

  return (
    <div>
      <ChoiceList items={languageList} selectedItem={language} onSelect={setLanguage} />
      &nbsp;&nbsp;
      {link}
      <br /><br />
      sort by: <ChoiceList items={allSortProps} selectedItem={sortBy} onSelect={changeSortBy} />
      &nbsp;&nbsp;
      filter to languages with: <ChoiceList items={allFilterProps} selectedItem={filterBy} onSelect={changeFilterBy} />
      <br />
      <CustomFilter applyFilter={applyCustomFilter} />
      {customError === '' ? undefined : <><br />unrecognized: {customError}</>}

      <br />
      <IpaChart lookup={lookup} combinedConsonants={true} diphthongs={stats.vowels} clicked={props.clicked} canClick={props.canClick} />
      <br />
      <SummaryChart stats={stats} onMouseOver={(ipa) => setHoveredSegment(ipa)} />
      <br />
      <SegmentFeatures segment={hoveredSegment} />
    </div>);
}

interface CustomFilterProps {
  readonly applyFilter: (value: string) => void;
}

/** UI for typing in a custom filter */
function CustomFilter(props: CustomFilterProps) {
  const [filterTo, setFilterTo] = useState('');
  const [dirty, setDirty] = useState(false);

  function onChange(s: string) {
    setFilterTo(s);
    setDirty(true);
  }

  function onApply() {
    props.applyFilter(filterTo);
    setDirty(false);
  }

  return (<>
    <label>
      limit to phonemes and features:&nbsp;&nbsp;
      <input type="text" size={30} value={filterTo}
        onChange={(event) => onChange(event.target.value)} />
    </label>
    &nbsp;&nbsp;
    <button onClick={onApply} disabled={!dirty}>apply</button>
  </>);
}
